/*
 * Copyright (c) 2025 BuyerView.ai
 * All rights reserved.
 * Unauthorized copying prohibited.
 */

import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { googleAnalytics } from './services/googleAnalytics';

// Critical path - load immediately
import { UserProvider } from './contexts/UserContext';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lightweight loading component
const QuickLoader = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="animate-pulse text-[#4ECDC4] text-lg">Loading...</div>
  </div>
);

// Ultra-lazy loading - everything is deferred
const Navigation = React.lazy(() => import('./components/ResponsiveNavigation').then(module => ({ default: module.ResponsiveNavigation })));
const Home = React.lazy(() => import('./pages/Home').then(module => ({ default: module.Home })));
const Search = React.lazy(() => import('./pages/Search').then(module => ({ default: module.Search })));
const AddReview = React.lazy(() => import('./pages/AddReview').then(module => ({ default: module.AddReview })));
const Auth = React.lazy(() => import('./pages/Auth').then(module => ({ default: module.Auth })));

// Less critical pages - only load when accessed
const EnhancedLocationVerify = React.lazy(() => import('./components/EnhancedLocationVerify').then(module => ({ default: module.EnhancedLocationVerify })));
const Profile = React.lazy(() => import('./pages/Profile').then(module => ({ default: module.Profile })));
const ReviewDetail = React.lazy(() => import('./pages/ReviewDetail').then(module => ({ default: module.ReviewDetail })));
const Terms = React.lazy(() => import('./pages/Terms').then(module => ({ default: module.Terms })));
const Privacy = React.lazy(() => import('./pages/Privacy').then(module => ({ default: module.Privacy })));
const Newsletter = React.lazy(() => import('./pages/Newsletter').then(module => ({ default: module.Newsletter })));
const FAQ = React.lazy(() => import('./pages/FAQ').then(module => ({ default: module.FAQ })));
const Guidelines = React.lazy(() => import('./pages/Guidelines').then(module => ({ default: module.Guidelines })));
const AgentDashboard = React.lazy(() => import('./pages/AgentDashboard').then(module => ({ default: module.AgentDashboard })));
const PropertyDetail = React.lazy(() => import('./pages/PropertyDetail').then(module => ({ default: module.PropertyDetail })));
const Blog = React.lazy(() => import('./pages/Blog').then(module => ({ default: module.Blog })));
const BlogPost = React.lazy(() => import('./pages/BlogPost').then(module => ({ default: module.BlogPost })));
const About = React.lazy(() => import('./pages/About').then(module => ({ default: module.About })));

// Simple Performance Monitor - load immediately in dev
import { SimplePerformanceMonitor } from './components/SimplePerformanceMonitor';
import { GATestPanel } from './components/GATestPanel';
import { SecurityAudit } from './components/SecurityAudit';

// Component to track page views
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    googleAnalytics.trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

export default function App() {
  return (
    <HelmetProvider>
    <UserProvider>
      <Router>
        <PageViewTracker />
        <div className="min-h-screen bg-gray-50 flex flex-col">
          {/* Navigation loads separately */}
          <Suspense fallback={<div className="h-16 bg-white shadow-sm"></div>}>
            <Navigation />
          </Suspense>

          <main className="flex-1">
            <ErrorBoundary>
            <Suspense fallback={<QuickLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/search" element={<Search />} />
                <Route path="/add-review" element={<AddReview />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/verify-location" element={<EnhancedLocationVerify />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/review/:id" element={<ReviewDetail />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/newsletter" element={<Newsletter />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/guidelines" element={<Guidelines />} />
                <Route path="/agent-dashboard" element={<AgentDashboard />} />
                <Route path="/property/:id" element={<PropertyDetail />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/about" element={<About />} />

                {/* Fallback route */}
                <Route path="*" element={<Home />} />
              </Routes>
            </Suspense>
            </ErrorBoundary>
          </main>

          {/* Development Tools - only visible in development */}
          {import.meta.env.DEV && (
            <>
              <SimplePerformanceMonitor />
              <GATestPanel />
              <SecurityAudit />
            </>
          )}

          {/* Footer is not critical - load last */}
          <Suspense fallback={<div className="h-20 bg-white"></div>}>
            {(() => {
              const Footer = React.lazy(() => import('./components/Footer').then(module => ({ default: module.Footer })));
              return <Footer />;
            })()}
          </Suspense>
        </div>
      </Router>
    </UserProvider>
    </HelmetProvider>
  );
}
